import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '@stores/authStore';
import './Login.css';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const Login = () => {
  const navigate = useNavigate();
  const { login, loading, error: authError, clearError } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema as any) as any,
  });

  const onSubmit = async (data: LoginFormData) => {
    clearError();
    const success = await login(data.email, data.password);
    if (success) {
      navigate('/');
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src="/src/assets/logo.gif" alt="Mal Bank" className="login-logo" />
          <h1 className="login-title">Operations Dashboard</h1>
          <p className="login-subtitle">Staff Access Only</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="login-form">
          {authError && (
            <div className="alert alert-error">
              {authError}
            </div>
          )}

          <div className="input-wrapper">
            <label htmlFor="email" className="input-label">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              {...register('email')}
              className={`input ${errors.email ? 'input-error' : ''}`}
              placeholder="staff@malbank.com"
              autoComplete="email"
              disabled={loading}
            />
            {errors.email && (
              <p className="input-error-message">{errors.email.message}</p>
            )}
          </div>

          <div className="input-wrapper">
            <label htmlFor="password" className="input-label">
              Password
            </label>
            <div className="input-with-icon">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                {...register('password')}
                className={`input ${errors.password ? 'input-error' : ''}`}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={loading}
              />
              <button
                type="button"
                className="input-icon-button"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {errors.password && (
              <p className="input-error-message">{errors.password.message}</p>
            )}
          </div>

          <div className="login-actions">
            <Link to="/forgot-password" className="link text-small">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-large"
            disabled={loading}
            style={{ width: '100%' }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <div className="login-footer">
            <p className="text-secondary text-small">
              Don't have an account?{' '}
              <Link to="/register" className="link">
                Register
              </Link>
            </p>
          </div>
        </form>
      </div>

      <p className="login-disclaimer text-small text-secondary">
        This system is for authorized Mal Bank staff only. All actions are logged and monitored.
      </p>
    </div>
  );
};

